import { createFileRoute } from "@tanstack/react-router";
import { isSameDay, startOfDay, subDays } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { DateNav } from "~/components/miscellaneous/date-nav";
import OnlineOrderItem from "~/components/order/online-order-item";
import { Button } from "~/components/ui/button";
import { useShift } from "~/hooks/use-shift";
import { api } from "~/lib/api";
import { cn, getMatchedShifts } from "~/lib/utils";
import {
  useBookingStore,
  useTempBookingStore,
} from "~/stores/use-booking-store";
import { useShiftStore } from "~/stores/use-shift-store";

export const Route = createFileRoute("/_main/online-orders/")({
  component: OnlineOrdersPage,
});

function OnlineOrdersPage() {
  const { selectedDate, setSelectedDate } = useTempBookingStore();

  const { data: onlineOrders } = api.order.getOnlineOrders.useQuery({
    startDate: startOfDay(subDays(selectedDate, 7)),
  });

  const { t } = useTranslation("common");

  const { layoutCalendarOpen, setLayoutCalendarOpen } = useBookingStore();

  const { data: day } = api.shift.getDay.useQuery(selectedDate, {
    staleTime: 15000,
  });

  const {
    setSelectedDate: setShiftStatusSelectedDate,
    setShiftStatusFormOpen,
  } = useShiftStore();

  const { shifts } = useShift();

  const matchedShifts = getMatchedShifts(shifts, selectedDate);

  const isClosed =
    day?.some((shift) => shift.closed) ?? matchedShifts.length === 0;

  const hasTempSlot =
    day
      ?.flatMap((shift) => shift.timeslots)
      .some((timeslot) => timeslot.temporary) ?? matchedShifts.length === 0;

  return (
    <div className="flex flex-grow flex-col overflow-hidden">
      <div className="flex flex-row items-center justify-between gap-6 border-b px-4 py-1">
        <div className="text-xs text-muted-foreground w-[350px] flex flex-row items-center gap-2">
          {/* <div>
            {isMobile && (
              <Trans
                key="reservations"
                i18nKey="Booking.reservations"
                values={{ reservations, guests, tables }}
                components={{
                  strong: <span key="strong" className="font-bold" />,
                }}
              />
            )}
            {!isMobile && (
              <Trans
                key="reservations_with_tables"
                i18nKey="Booking.reservations_with_tables"
                values={{ reservations, guests, tables }}
                components={{
                  strong: <span key="strong" className="font-bold" />,
                }}
              />
            )}
          </div> */}
        </div>

        <div className="md:flex hidden items-center justify-center flex-1">
          <DateNav
            futureDates={true}
            date={selectedDate}
            onNavigate={(nextDate) => setSelectedDate(nextDate)}
          />
        </div>
        <div className="flex flex-row items-center gap-2 w-[300px] justify-end">
          <Button
            onClick={() => {
              setShiftStatusFormOpen(true);
              setShiftStatusSelectedDate(selectedDate);
            }}
            className="p-1"
            variant="ghost"
          >
            <div
              className={cn(
                "before:absolute bg-green-500 w-2 h-2 rounded-full left-0 mr-2",
                hasTempSlot && "bg-yellow-500",
                isClosed && "bg-red-500",
              )}
            ></div>
            {isClosed ? t("closed") : t("open")}
          </Button>
          <Button
            onClick={() => {
              setLayoutCalendarOpen(!layoutCalendarOpen);
            }}
            variant="ghost"
            className="p-2 rounded-full"
          >
            <CalendarIcon />
          </Button>
          {/* <Popover
            open={bookingSearchOpen}
            onOpenChange={(open) => setBookingSearchOpen(open)}
          >
            <PopoverTrigger asChild>
              <Button variant="ghost" className="p-2 rounded-full">
                <SearchIcon />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0 min-w-[400px]">
              <div className="overflow-auto max-h-[65dvh]">
                <BookingSearch onClose={() => setBookingSearchOpen(false)} />
              </div>
            </PopoverContent>
          </Popover> */}
        </div>
      </div>
      <div className="flex flex-col-reverse md:flex-col">
        {!isSameDay(selectedDate, new Date()) && (
          <div className="w-full h-10">
            <Button
              className={cn(
                "text-sm p-1 h-10 animate-pulse w-full rounded-none -left-40",
              )}
              onClick={() => setSelectedDate(new Date())}
              variant="warning"
            >
              {t("DateNav.today")}
            </Button>
          </div>
        )}

        <div className="md:hidden flex items-center justify-center border-b">
          <DateNav
            futureDates={true}
            date={selectedDate}
            onNavigate={(nextDate) => setSelectedDate(nextDate)}
          />
        </div>
      </div>
      {onlineOrders && onlineOrders.length === 0 && (
        <div className="text-center font-bold flex items-center justify-center h-full">
          {t("OnlineOrders.no_orders_yet")}
        </div>
      )}
      <div className="flex overflow-auto flex-grow flex-col small-scrollbar">
        {onlineOrders?.map((onlineOrder) => (
          <div>
            <OnlineOrderItem order={onlineOrder} />
          </div>
        ))}
      </div>
    </div>
  );
}
